import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useSelectInterval } from "./useSelectInterval";

export interface RangeSelectorControlProps {
  range: {
    startDate: Date;
    endDate: Date;
  };
  intervalType: IntervalType;
  defaultIntervalType: IntervalType;
  isTimeBrushOpen: boolean;
  setIsTimeBrushOpen: Dispatch<SetStateAction<boolean>>;
  selectInterval: (type: IntervalType) => void;
  customIntervalChange: (start: Date, end: Date) => void;
  customIntervalSelect: (start: Date, end: Date) => void;
  isCustomRangeSelectOpened: boolean;
  setIsCustomRangeSelectOpened: Dispatch<SetStateAction<boolean>>;
}

export const rangeControlChanged = (r: RangeSelectorControlProps): boolean =>
  r.intervalType !== r.defaultIntervalType;

interface Attrs {
  onSelect?: (type: IntervalType, value: RangeType) => void;
  firstDate: Date;
  defaultIntervalType?: IntervalType;
  id?: string;
}
const LOCAL_STORAGE_PREFIX = "range-selector.";

export const useRangeSelectControl = ({
  onSelect,
  firstDate,
  defaultIntervalType = "oneYear",
  id = "",
}: Attrs): RangeSelectorControlProps => {
  const defaultInterval = useMemo(() => {
    try {
      const storedRawRange = localStorage.getItem(
        `${LOCAL_STORAGE_PREFIX}${id}`
      );
      const storedRange = storedRawRange ? JSON.parse(storedRawRange) : null;

      return storedRange
        ? {
            intervalType: storedRange.intervalType,
            startDate: new Date(storedRange.startDate),
            endDate: new Date(storedRange.endDate),
          }
        : {
            intervalType: defaultIntervalType,
            startDate: firstDate,
            endDate: firstDate,
          };
    } catch (err) {
      return {
        intervalType: defaultIntervalType,
        startDate: firstDate,
        endDate: firstDate,
      };
    }
    // eslint-disable-next-line
  }, [`${LOCAL_STORAGE_PREFIX}${id}`]);

  const { range, selectInterval, selectCustomInterval, intervalType } =
    useSelectInterval(
      defaultInterval.intervalType,
      defaultInterval.startDate,
      defaultInterval.endDate
    );

  const [isCustomRangeSelectOpened, setIsCustomRangeSelectOpened] =
    useState<boolean>(false);
  const [isTimeBrushOpen, setIsTimeBrushOpen] = useState<boolean>(false);

  const handleCustomIntervalChange = (start: Date, end: Date) => {
    selectCustomInterval(start, end);

    if (id) {
      localStorage.setItem(
        `${LOCAL_STORAGE_PREFIX}${id}`,
        JSON.stringify({
          intervalType: "custom",
          startDate: start,
          endDate: end,
        })
      );
    }
  };

  const handleTimeBrushSelect = (start: Date, end: Date) => {
    const newRange = selectCustomInterval(start, end);
    onSelect && onSelect("custom", newRange);

    if (id) {
      localStorage.setItem(
        `${LOCAL_STORAGE_PREFIX}${id}`,
        JSON.stringify({
          intervalType: "custom",
          startDate: newRange.startDate,
          endDate: newRange.endDate,
        })
      );
    }
  };

  const handleRangeSelect = (value: IntervalType) => {
    const newRange = selectInterval(value);
    onSelect && onSelect(value, newRange);

    if (id) {
      localStorage.setItem(
        `${LOCAL_STORAGE_PREFIX}${id}`,
        JSON.stringify({
          intervalType: value,
          startDate: newRange.startDate,
          endDate: newRange.endDate,
        })
      );
    }
  };

  const rangeStartDate = range.startDate || firstDate;

  return {
    range: { startDate: rangeStartDate, endDate: range.endDate },
    intervalType,
    defaultIntervalType,
    isTimeBrushOpen,
    setIsTimeBrushOpen,
    customIntervalSelect: handleTimeBrushSelect,
    selectInterval: handleRangeSelect,
    isCustomRangeSelectOpened: isCustomRangeSelectOpened ?? false,
    setIsCustomRangeSelectOpened,
    customIntervalChange: handleCustomIntervalChange,
  };
};
